import { useMemo } from "react";

import { useFacturasOptimized } from "~/hooks/useFacturasOptimized";
import { SalesRequestDocument } from "~/types/client";
import { resolveGross } from "~/utils/iva";

interface FacturaWithPayments extends SalesRequestDocument {
  totalPaid?: number;
  paymentProgress?: number;
}

/**
 * Optimized hook that processes payment data using the optimized facturas service
 * Payment calculations are now done in the database views for better performance
 */
export const useFacturasWithPaymentsOptimized = (
  forceOptimized: boolean = false
) => {
  const {
    facturas,
    loading,
    error,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    refreshFacturas,
    isOptimized,
  } = useFacturasOptimized(forceOptimized);

  // Process payment data - now mostly pre-calculated in the database views
  const facturasWithPayments = useMemo<FacturaWithPayments[]>(() => {
    if (!facturas || facturas.length === 0) {
      return [];
    }

    try {
      console.log(
        `⚡ PAYMENTS: Processing ${facturas.length} facturas with ${isOptimized ? "OPTIMIZED" : "ORIGINAL"} payment calculations`
      );

      const enhancedFacturas: FacturaWithPayments[] = facturas.map(
        (factura) => {
          // Payment data is now pre-calculated in the database views
          const totalPaid = (factura as any).paid_amount || 0;
          // Exempt-aware gross (facturas exentas SII 34 carry no IVA -> gross = net)
          const priceWithIva = resolveGross(factura as any);
          const totalAmount = (factura as any).total_amount || 0;

          // Use gross if available, otherwise fall back to total_amount
          const amountForCalculation =
            priceWithIva > 0 ? priceWithIva : totalAmount;

          // Calculate payment progress percentage
          const paymentProgress =
            amountForCalculation > 0
              ? Math.min(100, (totalPaid / amountForCalculation) * 100)
              : 0;

          return {
            ...factura,
            totalPaid,
            paymentProgress,
          };
        }
      );

      console.log(
        `✅ PAYMENTS: Successfully processed ${enhancedFacturas.length} facturas with payment data`
      );
      return enhancedFacturas;
    } catch (error) {
      console.error("❌ PAYMENTS: Error processing payment data:", error);
      return facturas as FacturaWithPayments[];
    }
  }, [facturas, isOptimized]);

  return {
    facturas: facturasWithPayments,
    loading,
    error,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    refreshFacturas,
    isOptimized, // Expose optimization status for debugging
  };
};
